import { useMemo } from 'react';
import _ from 'lodash';

import { useEnvironment } from '@/react/portainer/environments/queries';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';
import {
  UserAccessViewModel,
  TeamAccessViewModel,
} from '@/portainer/models/access';
import { useKubernetesRBACInitialValues } from '@/react/portainer/policies/components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { PortainerNamespaceAccessesConfigMap } from '@/react/kubernetes/configs/constants';
import { useConfigMap } from '@/react/kubernetes/configs/queries/useConfigMap';

import { NamespaceAccessesMap } from './types';

type NamespaceAccesses = {
  authorizedUsersAndTeams: Array<UserAccessViewModel | TeamAccessViewModel>;
  policyId: number;
  policyName: string;
  hasPolicyApplied: boolean;
};

/**
 * Extract namespace access information (both policy-based and ConfigMap-based)
 * Similar to useAccesses for environment access - handles all data transformation
 */
export function useNamespaceAccesses(namespaceName: string): NamespaceAccesses {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const environment = environmentQuery.data;

  // Reuse the same pattern as getRBACPolicy from useAccesses
  const policyId = environment?.Policies?.k8sRBACPolicy?.policyID;
  const policyName = environment?.Policies?.k8sRBACPolicy?.policyName;

  const policyQuery = usePolicy(policyId ?? 0);
  const { accessList } = useKubernetesRBACInitialValues(policyQuery.data);

  const usersQuery = useUsers(false, environmentId);
  const teamsQuery = useTeams(false, environmentId);
  const rolesQuery = useRbacRoles({
    select: (roles) => _.orderBy(roles, 'Priority', 'asc'),
  });
  const accessConfigMapQuery = useConfigMap(
    environmentId,
    PortainerNamespaceAccessesConfigMap.namespace,
    PortainerNamespaceAccessesConfigMap.configMapName
  );

  return useMemo(() => {
    const users = usersQuery.data || [];
    const teams = teamsQuery.data || [];
    const roles = rolesQuery.data || [];

    // Extract policy-based access for this namespace
    const policyAccessPolicies: Array<
      UserAccessViewModel | TeamAccessViewModel
    > = [];
    const hasPolicyApplied = !!(policyId && policyName && policyQuery.data);

    if (hasPolicyApplied) {
      policyAccessPolicies.push(
        ...accessList
          .filter(
            (access) =>
              access.namespaces && access.namespaces.includes(namespaceName)
          )
          .map((access) => {
            if (access.Type === 'user') {
              const user = users.find((u) => u.Id === access.Id);
              if (user) {
                return new UserAccessViewModel({
                  ...user,
                  Role: access.Role,
                });
              }
            } else {
              const team = teams.find((t) => t.Id === access.Id);
              if (team) {
                return new TeamAccessViewModel({
                  ...team,
                  Role: access.Role,
                });
              }
            }
            return null;
          })
          .filter(
            (access): access is UserAccessViewModel | TeamAccessViewModel =>
              access !== null
          )
      );
    }

    // Convert ConfigMap-based access to AccessViewModel format
    const configMapAccessPolicies: Array<
      UserAccessViewModel | TeamAccessViewModel
    > = [];

    if (
      accessConfigMapQuery.data &&
      users.length > 0 &&
      teams.length > 0 &&
      roles.length > 0
    ) {
      const namespacesAccesses: NamespaceAccessesMap = JSON.parse(
        accessConfigMapQuery.data?.Data?.[
          PortainerNamespaceAccessesConfigMap.accessKey
        ] ?? '{}'
      );
      const namespaceAccesses = namespacesAccesses[namespaceName];

      if (namespaceAccesses) {
        // Convert user access policies
        if (namespaceAccesses.UserAccessPolicies) {
          Object.entries(namespaceAccesses.UserAccessPolicies).forEach(
            ([userId, policy]) => {
              const user = users.find((u) => u.Id === parseInt(userId, 10));
              if (user) {
                const roleId = (policy as { RoleId: number }).RoleId;
                const role = roles.find((r) => r.Id === roleId);
                configMapAccessPolicies.push(
                  new UserAccessViewModel({
                    ...user,
                    Role: role
                      ? { Id: role.Id, Name: role.Name }
                      : { Id: roleId, Name: '' },
                  })
                );
              }
            }
          );
        }

        // Convert team access policies
        if (namespaceAccesses.TeamAccessPolicies) {
          Object.entries(namespaceAccesses.TeamAccessPolicies).forEach(
            ([teamId, policy]) => {
              const team = teams.find((t) => t.Id === parseInt(teamId, 10));
              if (team) {
                const roleId = (policy as { RoleId: number }).RoleId;
                const role = roles.find((r) => r.Id === roleId);
                configMapAccessPolicies.push(
                  new TeamAccessViewModel({
                    ...team,
                    Role: role
                      ? { Id: role.Id, Name: role.Name }
                      : { Id: roleId, Name: '' },
                  })
                );
              }
            }
          );
        }
      }
    }

    // When policy is applied, show only policy-based access (or empty if namespace not in policy)
    // Otherwise, show ConfigMap-based access
    const authorizedUsersAndTeams = hasPolicyApplied
      ? policyAccessPolicies
      : configMapAccessPolicies;

    return {
      authorizedUsersAndTeams,
      policyId: hasPolicyApplied ? policyId : 0,
      policyName: hasPolicyApplied ? policyName : '',
      hasPolicyApplied,
    };
  }, [
    policyId,
    policyName,
    policyQuery.data,
    accessList,
    usersQuery.data,
    teamsQuery.data,
    rolesQuery.data,
    accessConfigMapQuery.data,
    namespaceName,
  ]);
}
